import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Leaf, ArrowLeft, Bell, MoreVertical } from "lucide-react";
import { useObligaciones } from "@/hooks/useObligaciones";
import StatsCards from "@/components/dashboard/StatsCards";
import ObligacionesTable from "@/components/dashboard/ObligacionesTable";
import RealtimeIndicator from "@/components/RealtimeIndicator";
import { useAuth } from "@/hooks/useAuth";
import Footer from "@/components/Footer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const GestorAmbiental = () => {
  const navigate = useNavigate();
  const { obligaciones, loading, getEstadisticas, loadObligaciones } = useObligaciones('Ambiental');
  const { signOut, user } = useAuth();
  const stats = getEstadisticas();
  const [cardFilter, setCardFilter] = useState<'all' | 'Cumplido' | 'En Proceso' | 'Pendiente' | 'Vencido' | 'ProximasVencer'>('all');
  const [isAlertasOpen, setIsAlertasOpen] = useState(false);

  const handleCardFilterClick = (filterType: 'all' | 'Cumplido' | 'En Proceso' | 'Pendiente' | 'Vencido' | 'ProximasVencer') => {
    setCardFilter(filterType);
  };

  // Obtener obligaciones próximas a vencer
  const getProximasAVencer = () => {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    
    return obligaciones.filter(o => {
      if (o.estado === 'Cumplido' || o.estado === 'Vencido') return false;
      if (!o.vencimiento) return false;
      const vencimiento = new Date(o.vencimiento);
      return vencimiento >= today && vencimiento <= thirtyDaysFromNow;
    });
  };

  const proximasAVencer = getProximasAVencer();

  const getEstadoBadge = (estado: string) => {
    const variants = {
      'Cumplido': 'default',
      'En Proceso': 'secondary',
      'Pendiente': 'outline',
      'Vencido': 'destructive'
    } as const;

    return (
      <Badge variant={variants[estado as keyof typeof variants] || 'outline'}>
        {estado}
      </Badge>
    );
  };

  const getDiasRestantes = (vencimiento: string) => {
    const today = new Date();
    const fechaVencimiento = new Date(vencimiento);
    const diffTime = fechaVencimiento.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RealtimeIndicator />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-4">
          {/* Mobile/Tablet Layout */}
          <div className="lg:hidden relative flex flex-col items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="absolute top-0 right-0">
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                <DropdownMenuItem onClick={() => navigate("/ambiental")}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsAlertasOpen(true)}>
                  <Bell className="w-4 h-4 mr-2" />
                  Alertas ({stats.proximasAVencer})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={signOut}>
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Leaf className="w-10 h-10 text-environmental" />
            <div className="text-center">
              <h1 className="text-xl font-bold text-foreground">Gestor Ambiental PMA</h1>
              <p className="text-sm text-muted-foreground">Mina Río Claro</p>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/ambiental")}
                className="mr-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <Leaf className="w-8 h-8 text-environmental" />
              <div>
                <h1 className="text-xl font-bold text-foreground">Gestor Ambiental PMA</h1>
                <p className="text-sm text-muted-foreground">Mina Río Claro</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => setIsAlertasOpen(true)}>
                <Bell className="w-4 h-4 mr-2" />
                Alertas ({stats.proximasAVencer})
              </Button>
              <Button variant="outline" size="sm" onClick={signOut}>
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 lg:pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Welcome Section */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2">
                Dashboard de Gestión Ambiental
              </h2>
              <p className="text-lg text-muted-foreground">
                Sistema de seguimiento y control de obligaciones del Plan de Manejo Ambiental
              </p>
            </div>

            {/* Stats Cards */}
            <div className="mb-8">
              <StatsCards 
                stats={stats} 
                onFilterClick={handleCardFilterClick}
                activeFilter={cardFilter}
              />
            </div>

            {/* Obligaciones Table */}
            <ObligacionesTable 
              obligaciones={obligaciones}
              onRefresh={loadObligaciones}
              cardFilter={cardFilter}
              onClearFilter={() => setCardFilter('all')}
            />
            </div>
          </div>
        </main>

        {/* Dialog de Alertas */}
        <Dialog open={isAlertasOpen} onOpenChange={setIsAlertasOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-orange-600" />
                Obligaciones Próximas a Vencer (Próximos 30 días)
              </DialogTitle>
            </DialogHeader>
            
            {proximasAVencer.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay obligaciones próximas a vencer en los próximos 30 días</p>
              </div>
            ) : (
              <div className="space-y-3">
                {proximasAVencer.map((obligacion) => {
                  const diasRestantes = getDiasRestantes(obligacion.vencimiento);
                  return (
                    <Card key={obligacion.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="font-mono">
                                {obligacion.codigo}
                              </Badge>
                              {getEstadoBadge(obligacion.estado)}
                              <Badge 
                                className={`${
                                  diasRestantes <= 7 
                                    ? 'bg-red-100 text-red-700 border-red-200' 
                                    : diasRestantes <= 15 
                                    ? 'bg-orange-100 text-orange-700 border-orange-200'
                                    : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                }`}
                              >
                                {diasRestantes} {diasRestantes === 1 ? 'día' : 'días'} restantes
                              </Badge>
                            </div>
                            <h3 className="font-semibold text-sm mb-1">
                              {obligacion.nombre_programa}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-2">
                              {obligacion.objetivo}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>📅 Vencimiento: {new Date(obligacion.vencimiento).toLocaleDateString('es-ES')}</span>
                              <span>👤 {obligacion.responsable_interno}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Footer />
      </div>
  );
};

export default GestorAmbiental;
